import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/formStyles.css';
import { showAppAlert } from '../utils/alertService';
import { municipiosRJ } from '../utils/municipios';

const API_URL = process.env.REACT_APP_API_BASE_URL;

function maskCelular(value) {
  return value
    .replace(/\D/g, '')
    .replace(/^(\d{2})(\d)/g, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d+?$/, '$1');
}

function maskDate(value) {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\/\d{4})\d+?$/, '$1');
}

function EditClientModal({ client, visible, onClose, onClientUpdated }) {
  const [formData, setFormData] = useState(client || {});
  const [companies, setCompanies] = useState([]);
  const [policyData, setPolicyData] = useState({ signed: true, textPolicy: '', policy_id: null });
  const [isPolicyAccepted, setIsPolicyAccepted] = useState(false);
  const [isPolicyModalVisible, setIsPolicyModalVisible] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (client) {
      setFormData({
        ...client,
        status: client.status === 1 ? 'ativo' : 'inativo', // Converte 0/1 para string
        dataNascimento: (() => {
          if (!client.dataNascimento) return '';
          if (client.dataNascimento.includes('/')) return client.dataNascimento;
          const date = new Date(client.dataNascimento + 'T00:00:00');
          return !isNaN(date) ? date.toLocaleDateString('pt-BR') : '';
        })(),
      });
    } else {
      setFormData({});
    }
  }, [client]);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    let id = null;
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        id = parsedUser.user_id || parsedUser.id || parsedUser.id_usuario;
      } catch {
        id = null;
      }
    }

    if (id) {
      fetch(`${API_URL}/companies/by-user/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
        .then(res => res.json())
        .then(data => {
          const companiesArr = Array.isArray(data) ? data : [];
          setCompanies(companiesArr);

          if (client && client.company_id) {
            setFormData(prev => ({
              ...prev,
              company_id: client.company_id
            }));
          } else if (companiesArr.length === 1) {
            setFormData(prev => ({
              ...prev,
              company_id: companiesArr[0].id
            }));
          }
        })
        .catch(() => setCompanies([]));
    }
  }, [visible, client, token]);

  useEffect(() => {
    if (
      companies.length === 1 &&
      (!formData.company_id || formData.company_id === '')
    ) {
      setFormData(prev => ({
        ...prev,
        company_id: companies[0].id
      }));
    }
  }, [companies, formData.company_id]);

  useEffect(() => {
    if (visible) {
      const userData = localStorage.getItem('user');
      let userId = null;
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          userId = parsedUser.user_id || parsedUser.id || parsedUser.id_usuario;
        } catch {
          userId = null;
        }
      }

      if (userId && token) {
        fetch(`${API_URL}/policy/check/${userId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          })
          .then(res => res.json())
          .then(data => {
            if (data) {
              setPolicyData({
                signed: data.signed,
                textPolicy: data.textPolicy,
                policy_id: data.id, // Mapeia o 'id' da API para 'policy_id'
              });
            }
          })
          .catch(err => console.error("Failed to fetch policy", err));
      }
    }
  }, [visible, token]);

  if (!visible) {
    return null;
  }

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleBlur = () => {
    if (formData.dataNascimento && formData.dataNascimento.length === 10) {
      const parts = formData.dataNascimento.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        const birthDate = new Date(year, month, day);
        const tenYearsAgo = new Date();
        const oneHundredFiftyYearsAgo = new Date();
        tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
        oneHundredFiftyYearsAgo.setFullYear(oneHundredFiftyYearsAgo.getFullYear() - 150);

        if (birthDate > tenYearsAgo) {
          showAppAlert('warning', 'É necessário ter pelo menos 10 anos para se cadastrar.');
        } else if (birthDate < oneHundredFiftyYearsAgo) {
          showAppAlert('warning', 'A data de nascimento não pode ser superior a 150 anos.');
        }
      }
    }
  };

  const handleSave = async () => {
    if (!formData.id) {
      showAppAlert('error', 'ID do cliente não encontrado. Não é possível atualizar.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.(com|com\.br)$/i;
    if (!emailRegex.test(formData.email)) {
      showAppAlert('error', 'Por favor, insira um e-mail válido com domínio .com ou .com.br.');
      return;
    }

    // Validações para primeiro acesso
    if (Number(formData.primeiroAcesso) === 1) {
      if (!formData.senha || formData.senha.trim() === '') {
        showAppAlert('warning', 'A senha é obrigatória no primeiro acesso.');
        return;
      }
      if (!formData.genero) {
        showAppAlert('warning', 'O gênero é obrigatório no primeiro acesso.');
        return;
      }
      if (!formData.cidade || formData.cidade.trim() === '') {
        showAppAlert('warning', 'O município é obrigatório no primeiro acesso.');
        return;
      }
      if (!formData.bairro || formData.bairro.trim() === '') {
        showAppAlert('warning', 'O bairro é obrigatório no primeiro acesso.');
        return;
      }
    }

    if (policyData && !policyData.signed && !isPolicyAccepted) {
      showAppAlert('warning', 'Você deve aceitar os Termos e Políticas para continuar.');
      return;
    }

    // Converte a data do formato dd/mm/yyyy para yyyy-mm-dd antes de enviar
    let formattedDate = null;
    if (formData.dataNascimento) {
      const parts = formData.dataNascimento.split('/');
      if (parts.length === 3) {
        formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }

    let payload = {
      nome: formData.nome,
      telefone: (formData.telefone || '').replace(/\D/g, ''),
      dataNascimento: formattedDate,
      genero: formData.genero,
      email: formData.email,
      company_id: formData.company_id,
      cidade: formData.cidade,
      bairro: formData.bairro,
      status: formData.status === 'ativo' ? 1 : 0, // Converte string para 0/1
    };

    // Adiciona a flag de aceite da política se aplicável
    if (policyData && !policyData.signed && isPolicyAccepted) {
      payload = {
        ...payload,
        signed: true,
        policy_id: policyData.policy_id,
      };
    }
    // Adiciona a senha apenas se ela foi preenchida
    if (formData.senha && formData.senha.trim() !== '') {
      payload.password = formData.senha;
    }

    // Se foi o primeiro acesso, envia a flag para desativá-la no backend
    if (Number(formData.primeiroAcesso) === 1) {
      payload.primeiroAcesso = 0;
      payload.status = 1;
    }

    const url = `${API_URL}/clients/${formData.id}`;

    try {
      const response = await axios.put(url, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        showAppAlert('success', 'Cliente atualizado com sucesso!');
        const userData = localStorage.getItem('user');
        if (userData) {
          const parsedUser = JSON.parse(userData);
          parsedUser.primeiroAcesso = 0;
          localStorage.setItem('user', JSON.stringify(parsedUser));
        }
        setTimeout(() => {
          onClose();
          if (onClientUpdated) onClientUpdated();
        }, 1500);
      } else {
        showAppAlert('error', 'Erro ao atualizar cliente.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Erro ao atualizar cliente.';
      showAppAlert('error', errorMessage);
    }
  };  

  const perfil = (() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        return parsedUser.perfil;
      } catch {
        return '';
      }
    }
    return '';
  })();

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3 style={{
          borderBottom: '2px solid #C8A2C8',
          paddingBottom: '5px',
          fontSize: 'clamp(1.2rem, 2vw, 1.5rem)'
        }}>Editar Cliente</h3>
        <form 
          onSubmit={async (e) => {
            e.preventDefault();
            await handleSave();
          }}
        >
          <input type="hidden" name="id" value={formData.id || ''} />
          <input type="hidden" name="primeiroAcesso" value={formData.primeiroAcesso !== undefined ? String(formData.primeiroAcesso) : ''} />

          {/* Nome */}
          <div className="label-style">
            <label>Nome:</label>
            <input
              type="text"
              name="nome"
              value={formData.nome || ''}
              onChange={handleChange}
              className="input-style input-medium"
              required
            />
          </div>

          {/* E-mail */}
          <div className="label-style">
            <label>E-mail:</label>
            <input
              type="email"
              name="email"
              value={formData.email || ''}
              onChange={handleChange}
              className="input-style input-medium"
              readOnly
              disabled
              required
              title="O E-mail não pode ser editado."
            />
          </div>

          {/* Senha */}
          {perfil === 'cliente' && (
            <div className="label-style">
              <label>Senha:</label>
              <span style={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: '400px' }}>
                <input
                  type={isPasswordVisible ? 'text' : 'password'}
                  name="senha"
                  value={formData.senha || ''}
                  onChange={handleChange}
                  className="input-style input-medium"
                  autoComplete="new-password"
                  required={Number(formData.primeiroAcesso) === 1}
                />
                <span
                  onMouseDown={() => setIsPasswordVisible(true)}
                  onMouseUp={() => setIsPasswordVisible(false)}
                  onMouseLeave={() => setIsPasswordVisible(false)}
                  onTouchStart={() => setIsPasswordVisible(true)}
                  onTouchEnd={() => setIsPasswordVisible(false)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8A2BE2"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </span>
              </span>
            </div>
          )}

          {/* Telefone */}
          <div className="label-style">
            <label>Celular:</label>
            <input
              type="text"
              name="telefone"
              value={maskCelular(formData.telefone || '')}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  telefone: maskCelular(e.target.value)
                }))
              }
              className="input-style input-medium"
            />
          </div>

          {/* Data de Nascimento */}
          <div className="label-style">
            <label>Data de Nascimento:</label>
            <input
              type="text"
              name="dataNascimento"
              value={formData.dataNascimento || ''}
              onChange={e => setFormData(prev => ({ ...prev, dataNascimento: maskDate(e.target.value) }))}
              onBlur={handleBlur}
              className="input-style input-medium"
              placeholderText="dd/mm/aaaa"
              maxLength="10"
              required={Number(formData.primeiroAcesso) === 1}
            />
          </div>

          {/* Status — oculto para perfil cliente */}
          {perfil !== 'cliente' && (
          <div className="label-style">
            <label>Status:</label>
            <select
              name="status"
              value={formData.status || ''}
              onChange={handleChange}
              className="input-style input-medium"
              required
            >
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
          </div>
          )}
          {/* Gênero e Empresa */}
          <div className="form-row">
            <div className="label-style flex-1">
              <label>Gênero:</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="genero"
                    value="masculino"
                    checked={formData.genero === 'masculino'}
                    onChange={handleChange}
                    required={Number(formData.primeiroAcesso) === 1}
                  />
                  Masculino
                </label>
                <label>
                  <input
                    type="radio"
                    name="genero"
                    value="feminino"
                    checked={formData.genero === 'feminino'}
                    onChange={handleChange}
                  />
                  Feminino
                </label>
                <label>
                  <input
                    type="radio"
                    name="genero"
                    value="outro"
                    checked={formData.genero === 'outro'}
                    onChange={handleChange}
                  />
                  Outro
                </label>
              </div>
            </div>
            <div className="label-style flex-1" style={{ display: 'none' }}>
              <label>Empresa:</label>
              <select
                name="company_id"
                value={formData.company_id || ''}
                className="input-style"
                disabled
                readOnly
                required
              >
                <option value="">Selecione a empresa...</option>
                {companies
                  .filter(company => String(company.id) === String(formData.company_id))
                  .map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.nomeFantasia}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Município e Bairro */}
          <div className="form-row">
            <div className="label-style flex-1">
              <label>Município:</label>
              <select
                name="cidade"
                value={formData.cidade || ''}
                onChange={handleChange}
                className="input-style"
                required={Number(formData.primeiroAcesso) === 1}
              >
                <option value="">Selecione um município...</option>
                {municipiosRJ.map((mun, index) => (
                  <option key={index} value={mun}>
                    {mun}
                  </option>
                ))}
              </select>
            </div>
            <div className="label-style flex-1">
              <label>Bairro:</label>
              <input
                type="text"
                name="bairro"
                value={formData.bairro || ''}
                onChange={handleChange}
                className="input-style input-small"
                required={Number(formData.primeiroAcesso) === 1}
              />
            </div>
          </div>

          {policyData && !policyData.signed && (
            <div className="label-style" style={{ display: 'flex', alignItems: 'center', marginTop: '15px' }}>
              <input
                type="checkbox"
                id="acceptPolicy"
                style={{ width: 'auto', margin: '0 8px 0 0' }}
                checked={isPolicyAccepted}
                onChange={(e) => setIsPolicyAccepted(e.target.checked)}
                required
              />
              <label htmlFor="acceptPolicy" style={{ marginBottom: 0, fontWeight: 'normal' }}>
                Eu li e aceito os{' '}
                <button
                  type="button"
                  onClick={() => setIsPolicyModalVisible(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: '#007bff',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  Termos e Políticas
                </button>
              </label>
            </div>
          )}

          <div className="button-container">
            <button type="submit" className="button-style">Salvar</button>
            <button
              type="button"
              onClick={onClose}
              className="button-style cancel"
              disabled={Number(formData.primeiroAcesso) === 1}
            >
              Cancelar
            </button>
          </div>
        </form>

        {isPolicyModalVisible && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Termos e Políticas</h3>
              <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }} dangerouslySetInnerHTML={{ __html: policyData.textPolicy }}></div>
              <button onClick={() => setIsPolicyModalVisible(false)} className="button-style">Fechar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EditClientModal;
